import React, { useState, useEffect } from 'react';
import { collection, doc, getDocs, updateDoc, addDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { getSortedPlansByRank, getPlanDropdownOptions, createPlanBadge, getPlanRanking } from '../../utils/planRankingUtils';
import { getPlanPackagesByRole } from '../../data/planPackages';

const PlanConfigPanel = () => {
  const [planConfigs, setPlanConfigs] = useState({
    customer: {},
    girl: {},
    momo: {}
  });
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('config'); // config, promotions, analytics
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editingPromotion, setEditingPromotion] = useState(null);

  // Form states for plan configuration
  const [planForm, setPlanForm] = useState({
    role: 'customer',
    planKey: '',
    name: '',
    price: 0,
    features: [],
    color: 'bg-gray-100 text-gray-800',
    maxImages: 1,
    dailyViews: 1,
    monthlyBookings: 0,
    revenueShare: 0,
    isActive: true,
    trialDays: 0
  });

  // Form states for promotion
  const [promotionForm, setPromotionForm] = useState({
    name: '',
    description: '',
    discountType: 'percentage', // percentage, fixed
    discountValue: 0,
    targetRole: 'all', // all, customer, girl, momo
    targetPlan: 'all', // all, specific plan
    startDate: '',
    endDate: '',
    maxUses: 0,
    isActive: true,
    promoCode: ''
  });

  // Auto generate promo code
  const generatePromoCode = () => {
    const prefix = 'PLAN';
    const randomNum = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    const generatedCode = `${prefix}${randomNum}`;
    setPromotionForm({...promotionForm, promoCode: generatedCode});
  };

  // Default plan configurations
  const defaultPlanConfigs = {
    customer: {
      free: { 
        name: 'Free', 
        price: 0, 
        features: ['ดูรูป 1 รูป/วัน'], 
        color: 'bg-gray-100 text-gray-800',
        maxImages: 0,
        dailyViews: 1,
        monthlyBookings: 0,
        revenueShare: 0,
        isActive: true,
        trialDays: 0
      },
      basic: { 
        name: 'Basic', 
        price: 100, 
        features: ['ดูรูป 5 รูป/วัน', 'จองได้ 3 ครั้ง/เดือน'], 
        color: 'bg-blue-100 text-blue-800',
        maxImages: 0,
        dailyViews: 5,
        monthlyBookings: 3,
        revenueShare: 0,
        isActive: true,
        trialDays: 7
      },
      silver: { 
        name: 'Silver', 
        price: 300, 
        features: ['ดูรูป 15 รูป/วัน', 'จองได้ 10 ครั้ง/เดือน'], 
        color: 'bg-gray-100 text-gray-800',
        maxImages: 0,
        dailyViews: 15,
        monthlyBookings: 10,
        revenueShare: 0,
        isActive: true,
        trialDays: 3
      },
      gold: { 
        name: 'Gold', 
        price: 500, 
        features: ['ดูรูปไม่จำกัด', 'จองได้ 20 ครั้ง/เดือน'], 
        color: 'bg-yellow-100 text-yellow-800',
        maxImages: 0,
        dailyViews: -1, // unlimited
        monthlyBookings: 20,
        revenueShare: 0,
        isActive: true,
        trialDays: 0
      },
      platinum: { 
        name: 'Platinum', 
        price: 1000, 
        features: ['ดูรูปไม่จำกัด', 'จองได้ไม่จำกัด', 'สิทธิพิเศษ'], 
        color: 'bg-purple-100 text-purple-800',
        maxImages: 0,
        dailyViews: -1,
        monthlyBookings: -1,
        revenueShare: 0,
        isActive: true,
        trialDays: 0
      }
    },
    girl: {
      free: { 
        name: 'Free', 
        price: 0, 
        features: ['อัพโหลดรูป 1 รูป'], 
        color: 'bg-gray-100 text-gray-800',
        maxImages: 1,
        dailyViews: 0,
        monthlyBookings: 5,
        revenueShare: 0.3,
        isActive: true,
        trialDays: 0
      },
      basic: { 
        name: 'Basic', 
        price: 100, 
        features: ['อัพโหลดรูป 3 รูป', 'รายได้เพิ่ม 10%'], 
        color: 'bg-blue-100 text-blue-800',
        maxImages: 3,
        dailyViews: 0,
        monthlyBookings: 15,
        revenueShare: 0.35,
        isActive: true,
        trialDays: 7
      },
      premium: { 
        name: 'Premium', 
        price: 300, 
        features: ['อัพโหลดรูป 6 รูป', 'รายได้เพิ่ม 20%'], 
        color: 'bg-green-100 text-green-800',
        maxImages: 6,
        dailyViews: 0,
        monthlyBookings: 30,
        revenueShare: 0.4,
        isActive: true,
        trialDays: 3
      },
      vip: { 
        name: 'VIP', 
        price: 500, 
        features: ['อัพโหลดรูปไม่จำกัด', 'รายได้เพิ่ม 30%', 'สิทธิพิเศษ'], 
        color: 'bg-purple-100 text-purple-800',
        maxImages: -1,
        dailyViews: 0,
        monthlyBookings: 50,
        revenueShare: 0.5,
        isActive: true,
        trialDays: 0
      }
    },
    momo: {
      starter: { 
        name: 'Starter', 
        price: 200, 
        features: ['จัดการทีม 3 คน', 'รายได้ 5%'], 
        color: 'bg-gray-100 text-gray-800',
        maxImages: 0,
        dailyViews: 0,
        monthlyBookings: 10,
        revenueShare: 0.05,
        isActive: true,
        trialDays: 7
      },
      basic: { 
        name: 'Basic', 
        price: 400, 
        features: ['จัดการทีม 10 คน', 'รายได้ 8%'], 
        color: 'bg-blue-100 text-blue-800',
        maxImages: 0,
        dailyViews: 0,
        monthlyBookings: 25,
        revenueShare: 0.08,
        isActive: true,
        trialDays: 3
      },
      pro: { 
        name: 'Pro', 
        price: 800, 
        features: ['จัดการทีม 25 คน', 'รายได้ 12%'], 
        color: 'bg-green-100 text-green-800',
        maxImages: 0,
        dailyViews: 0,
        monthlyBookings: 50,
        revenueShare: 0.12,
        isActive: true,
        trialDays: 0
      },
      enterprise: { 
        name: 'Enterprise', 
        price: 1500, 
        features: ['จัดการทีมไม่จำกัด', 'รายได้ 15%'], 
        color: 'bg-purple-100 text-purple-800',
        maxImages: 0,
        dailyViews: 0,
        monthlyBookings: 100,
        revenueShare: 0.15,
        isActive: true,
        trialDays: 0
      },
      ultraEnterprise: { 
        name: 'Ultra Enterprise', 
        price: 3000, 
        features: ['จัดการทีมไม่จำกัด', 'รายได้ 20%', 'สิทธิพิเศษ'], 
        color: 'bg-yellow-100 text-yellow-800',
        maxImages: 0,
        dailyViews: 0,
        monthlyBookings: -1,
        revenueShare: 0.2,
        isActive: true,
        trialDays: 0
      }
    }
  };

  // Load plan configurations
  useEffect(() => {
    loadPlanConfigs();
    loadPromotions();
  }, []);

  const loadPlanConfigs = async () => {
    try {
      setLoading(true);
      
      // Load from Firestore or use default
      const configsRef = collection(db, 'planConfigs');
      const snapshot = await getDocs(configsRef);
      
      if (snapshot.empty) {
        // Initialize with default configurations
        setPlanConfigs(defaultPlanConfigs);
        await initializeDefaultPlanConfigs();
      } else {
        const configs = {};
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          configs[data.role] = data.packages;
        });
        setPlanConfigs(configs);
      }
    } catch (error) {
      console.error('Error loading plan configs:', error);
      setPlanConfigs(defaultPlanConfigs);
    } finally {
      setLoading(false);
    }
  };

  const initializeDefaultPlanConfigs = async () => {
    try {
      for (const [role, packages] of Object.entries(defaultPlanConfigs)) {
        await addDoc(collection(db, 'planConfigs'), {
          role,
          packages,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
      console.log('✅ Default plan configurations initialized');
    } catch (error) {
      console.error('Error initializing default plan configs:', error);
    }
  };

  const loadPromotions = async () => {
    try {
      const promotionsRef = collection(db, 'promotions');
      const snapshot = await getDocs(query(promotionsRef, orderBy('createdAt', 'desc')));
      const promotionsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPromotions(promotionsData);
    } catch (error) {
      console.error('Error loading promotions:', error);
    }
  };

  const savePlanConfig = async () => {
    try {
      const configData = {
        ...planForm,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (editingPlan) {
        await updateDoc(doc(db, 'planConfigs', editingPlan.id), configData);
      } else {
        await addDoc(collection(db, 'planConfigs'), configData);
      }

      setShowConfigModal(false);
      setPlanForm({
        role: 'customer',
        planKey: '',
        name: '',
        price: 0,
        features: [],
        color: 'bg-gray-100 text-gray-800',
        maxImages: 1,
        dailyViews: 1,
        monthlyBookings: 0,
        revenueShare: 0,
        isActive: true,
        trialDays: 0
      });
      setEditingPlan(null);
      loadPlanConfigs();
    } catch (error) {
      console.error('Error saving plan config:', error);
    }
  };

  const deletePlanConfig = async (configId) => {
    try {
      await deleteDoc(doc(db, 'planConfigs', configId));
      loadPlanConfigs();
    } catch (error) {
      console.error('Error deleting plan config:', error);
    }
  };

  const resetPlanForm = () => {
    setPlanForm({
      role: 'customer',
      planKey: '',
      name: '',
      price: 0,
      features: [],
      color: 'bg-gray-100 text-gray-800',
      maxImages: 1,
      dailyViews: 1,
      monthlyBookings: 0,
      revenueShare: 0,
      isActive: true,
      trialDays: 0
    });
    setEditingPlan(null);
  };

  const openEditPlanModal = (plan) => {
    setPlanForm(plan);
    setEditingPlan(plan);
    setShowConfigModal(true);
  };

  const getPlanDropdownOptions = (role) => {
    const packages = planConfigs[role] || {};
    return Object.entries(packages).map(([key, pkg]) => ({
      value: key,
      label: pkg.name
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">⚙️ จัดการ Plan</h2>
        
        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-6 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('config')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
              activeTab === 'config'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            ⚙️ Config Plans
          </button>
          <button
            onClick={() => setActiveTab('promotions')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
              activeTab === 'promotions'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            🎁 Promotions
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex-1 py-2 px-4 rounded-md font-medium transition-colors ${
              activeTab === 'analytics'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            📊 Analytics
          </button>
        </div>

        {/* Plan Configuration Tab */}
        {activeTab === 'config' && (
          <div className="space-y-6">
            {Object.entries(planConfigs).map(([role, packages]) => (
              <div key={role} className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4 capitalize">
                  {role === 'customer' && '👤 Customer Plans'}
                  {role === 'girl' && '👩 Girl Plans'}
                  {role === 'momo' && '👨‍💼 Manager Plans'}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(packages).map(([key, pkg]) => (
                    <div key={key} className="border rounded-lg p-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getPlanRanking(role, key)?.icon}</span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanRanking(role, key)?.color}`}>
                            {pkg.name}
                          </span>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            Rank {getPlanRanking(role, key)?.rank}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => openEditPlanModal({...pkg, key, role})}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => deletePlanConfig(pkg.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="text-lg font-bold text-green-600">
                          {pkg.price === 0 ? 'ฟรี' : `${pkg.price} เหรียญ`}
                        </div>
                        
                        <div className="text-sm text-gray-600">
                          <div>📷 รูปสูงสุด: {pkg.maxImages === -1 ? 'ไม่จำกัด' : `${pkg.maxImages} รูป`}</div>
                          <div>👁️ ดูรูป/วัน: {pkg.dailyViews === -1 ? 'ไม่จำกัด' : `${pkg.dailyViews} รูป`}</div>
                          <div>📅 จอง/เดือน: {pkg.monthlyBookings === -1 ? 'ไม่จำกัด' : `${pkg.monthlyBookings} ครั้ง`}</div>
                          <div>💰 รายได้: {pkg.revenueShare * 100}%</div>
                        </div>
                        
                        <div className="text-xs text-gray-500">
                          {pkg.features.map((feature, index) => (
                            <div key={index}>• {feature}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            <button
              onClick={() => setShowConfigModal(true)}
              className="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
            >
              ➕ เพิ่ม Plan ใหม่
            </button>
          </div>
        )}

        {/* Promotions Tab */}
        {activeTab === 'promotions' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">🎁 Promotions</h3>
              <button
                onClick={() => setShowPromotionModal(true)}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
              >
                ➕ เพิ่ม Promotion
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {promotions.map((promo) => (
                <div key={promo.id} className="border rounded-lg p-4 hover:shadow-md transition">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-semibold">{promo.name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      promo.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {promo.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div>🎯 Target: {promo.targetRole} - {promo.targetPlan}</div>
                    <div>💰 Discount: {promo.discountValue}{promo.discountType === 'percentage' ? '%' : ' เหรียญ'}</div>
                    <div>📅 Period: {promo.startDate} - {promo.endDate}</div>
                    <div>🔢 Max Uses: {promo.maxUses === 0 ? 'ไม่จำกัด' : promo.maxUses}</div>
                    <div>🎫 Code: <code className="bg-gray-100 px-1 rounded">{promo.promoCode}</code></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold">📊 Plan Analytics</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900">Total Plans</h4>
                <div className="text-2xl font-bold text-blue-600">
                  {Object.values(planConfigs).reduce((total, packages) => total + Object.keys(packages).length, 0)}
                </div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900">Active Plans</h4>
                <div className="text-2xl font-bold text-green-600">
                  {Object.values(planConfigs).reduce((total, packages) => 
                    total + Object.values(packages).filter(pkg => pkg.isActive).length, 0)}
                </div>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-900">Total Promotions</h4>
                <div className="text-2xl font-bold text-purple-600">{promotions.length}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Plan Configuration Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingPlan ? '✏️ แก้ไข Plan' : '➕ เพิ่ม Plan ใหม่'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={planForm.role}
                  onChange={(e) => setPlanForm({...planForm, role: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="customer">Customer</option>
                  <option value="girl">Girl</option>
                  <option value="momo">Manager</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan Key</label>
                <input
                  type="text"
                  value={planForm.planKey}
                  onChange={(e) => setPlanForm({...planForm, planKey: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="free, basic, premium, vip"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={planForm.name}
                  onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Free, Basic, Premium, VIP"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price (เหรียญ)</label>
                <input
                  type="number"
                  value={planForm.price}
                  onChange={(e) => setPlanForm({...planForm, price: parseInt(e.target.value)})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  min="0"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Images</label>
                <input
                  type="number"
                  value={planForm.maxImages}
                  onChange={(e) => setPlanForm({...planForm, maxImages: parseInt(e.target.value)})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  min="-1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Daily Views</label>
                <input
                  type="number"
                  value={planForm.dailyViews}
                  onChange={(e) => setPlanForm({...planForm, dailyViews: parseInt(e.target.value)})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  min="-1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Bookings</label>
                <input
                  type="number"
                  value={planForm.monthlyBookings}
                  onChange={(e) => setPlanForm({...planForm, monthlyBookings: parseInt(e.target.value)})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  min="-1"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Share (%)</label>
                <input
                  type="number"
                  value={planForm.revenueShare}
                  onChange={(e) => setPlanForm({...planForm, revenueShare: parseFloat(e.target.value)})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  min="0"
                  max="1"
                  step="0.01"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={planForm.isActive}
                  onChange={(e) => setPlanForm({...planForm, isActive: e.target.checked})}
                  className="mr-2"
                />
                <label className="text-sm font-medium text-gray-700">Active</label>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={savePlanConfig}
                className="flex-1 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
              >
                💾 บันทึก
              </button>
              <button
                onClick={() => {
                  setShowConfigModal(false);
                  resetPlanForm();
                }}
                className="flex-1 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
              >
                ❌ ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Promotion Modal */}
      {showPromotionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">🎁 เพิ่ม Promotion</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={promotionForm.name}
                  onChange={(e) => setPromotionForm({...promotionForm, name: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={promotionForm.description}
                  onChange={(e) => setPromotionForm({...promotionForm, description: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  rows="3"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Role</label>
                <select
                  value={promotionForm.targetRole}
                  onChange={(e) => setPromotionForm({...promotionForm, targetRole: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="all">All</option>
                  <option value="customer">Customer</option>
                  <option value="girl">Girl</option>
                  <option value="momo">Manager</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Plan</label>
                <select
                  value={promotionForm.targetPlan}
                  onChange={(e) => setPromotionForm({...promotionForm, targetPlan: e.target.value})}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="all">All Plans</option>
                  {getPlanDropdownOptions(promotionForm.targetRole).map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Promo Code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promotionForm.promoCode}
                    onChange={(e) => setPromotionForm({...promotionForm, promoCode: e.target.value})}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                  />
                  <button
                    onClick={generatePromoCode}
                    className="px-3 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition"
                  >
                    🎲 Generate
                  </button>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowPromotionModal(false)}
                className="flex-1 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
              >
                💾 บันทึก
              </button>
              <button
                onClick={() => setShowPromotionModal(false)}
                className="flex-1 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
              >
                ❌ ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanConfigPanel;




